import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GolfReservationDeleteModal from './GolfReservationDeleteModal';
import { useMutation } from '@tanstack/react-query';
import { updateGolfReservation } from '../../../api/golf';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';

const GolfReservationManageModal = ({ open, handleClose, reservationData }) => {
  const [usageTime, setUsageTime] = useState(30);
  const [bufferTime, setBufferTime] = useState(3);
  const [memberName, setMemberName] = useState('');
  const [ticketName, setTicketName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [remainingTime, setRemainingTime] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // 예약 상태 확인: 과거 내역인지 판단 (RUNNING, RESERVED, WAITING이 아닌 경우)
  const status = reservationData?.status || reservationData?.reservationStatus;
  const isPastHistory = status && !['RUNNING', 'RESERVED', 'WAITING'].includes(status);
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  // 시간 포맷팅 함수
  const formatDateTime = (dateTimeString, includeSeconds = false) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      if (includeSeconds) {
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      }
      return `${hours}:${minutes}`;
    } catch {
      return dateTimeString;
    }
  };

  // 날짜 포맷팅 함수
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateString;
    }
  };

  // 시간 입력 유효성 검사 및 포맷팅 함수 (HH:MM 형식 강제)
  const formatTimeInput = value => {
    // 콜론 제거 후 숫자만 추출
    let replaceTime = value.replace(/:/g, '');
    replaceTime = replaceTime.replace(/\D/g, '');

    // 4자리 이상이면 4자리로 제한
    if (replaceTime.length > 4) {
      replaceTime = replaceTime.substring(0, 4);
    }

    // 4자리면 HH:MM 형식으로 변환
    if (replaceTime.length >= 4) {
      const hours = replaceTime.substring(0, 2);
      const minute = replaceTime.substring(2, 4);

      // 유효성 검사
      if (!isFinite(hours + minute)) {
        return '00:00';
      }
      if (parseInt(hours + minute) >= 2400) {
        return '23:59';
      }
      if (parseInt(minute) >= 60) {
        return hours + ':00';
      }
      if (parseInt(hours) >= 24) {
        return '23:' + minute;
      }
      return hours + ':' + minute;
    }

    // 2자리 이상이면 자동으로 콜론 추가
    if (replaceTime.length >= 2) {
      const hours = replaceTime.substring(0, 2);
      const minute = replaceTime.substring(2);
      if (minute) {
        return hours + ':' + minute;
      }
      return hours + ':';
    }

    return replaceTime;
  };

  // 이름에서 숫자 제거
  const formatMemberName = memberName => {
    if (!memberName) return '';
    return memberName.replace(/\d+/g, '');
  };

  // 종료시간 계산 함수 (시작시간 + 이용시간 + 여유시간)
  const calculateEndTime = (startTimeStr, usageMinutes, bufferMinutes) => {
    if (!startTimeStr || !startTimeStr.includes(':')) return '';

    try {
      const [hours, minutes] = startTimeStr.split(':').map(Number);

      // 숫자로 변환 (문자열일 수 있음)
      const usage = Number(usageMinutes) || 0;
      const buffer = Number(bufferMinutes) || 0;

      // 총 시간 = 이용시간 + 여유시간
      const totalMinutes = usage + buffer;

      // 시작시간을 분으로 변환
      const startTotalMinutes = hours * 60 + minutes;

      // 종료시간 계산 (분 단위)
      const endTotalMinutes = startTotalMinutes + totalMinutes;

      // 시간과 분으로 변환
      const endHours = Math.floor(endTotalMinutes / 60) % 24;
      const endMinutes = endTotalMinutes % 60;

      return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
    } catch (error) {
      console.error('종료시간 계산 오류:', error);
      return '';
    }
  };

  // 시간 문자열을 날짜+시간 형식으로 변환 (예: "16:30" -> "2025-12-31 16:30:00")
  const formatTimeToDateTime = (timeString, originalDateTime) => {
    if (!timeString) return null;

    try {
      let date;

      // 원본 날짜가 있으면 그 날짜를 사용, 없으면 오늘 날짜 사용
      if (originalDateTime) {
        date = new Date(originalDateTime);
      } else {
        date = new Date();
      }

      // 시간 문자열 파싱 (HH:MM 형식)
      const [hours, minutes] = timeString.split(':').map(Number);

      // 날짜 설정
      date.setHours(hours || 0, minutes || 0, 0, 0);

      // YYYY-MM-DD HH:mm:ss 형식으로 변환
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hoursStr = String(date.getHours()).padStart(2, '0');
      const minutesStr = String(date.getMinutes()).padStart(2, '0');
      const secondsStr = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hoursStr}:${minutesStr}:${secondsStr}`;
    } catch (error) {
      console.error('시간 변환 오류:', error);
      return null;
    }
  };

  // reservationData가 변경될 때 필드 업데이트
  useEffect(() => {
    if (reservationData) {
      setMemberName(formatMemberName(reservationData.memberName) || '');
      setTicketName(reservationData.coursePassName || '');
      const initialUsageTime = reservationData.usageMinute || 30;
      const initialBufferTime = reservationData.bufferMinute || 0;
      setUsageTime(initialUsageTime);
      setBufferTime(initialBufferTime);
      const initialStartTime = formatDateTime(reservationData.startTime) || '';
      setStartTime(initialStartTime);
      // 종료시간은 시작시간 + 이용시간 + 여유시간으로 계산
      const calculatedEndTime = calculateEndTime(initialStartTime, initialUsageTime, initialBufferTime);
      setEndTime(calculatedEndTime || formatDateTime(reservationData.endTime) || '');
      setExpirationDate(formatDate(reservationData.passEndTime) || '');
      // 이용권 잔여시간은 계산이 필요할 수 있으므로 일단 빈 값으로 설정
      setRemainingTime('');
    }
  }, [reservationData]);

  // 시작시간, 이용시간, 여유시간이 변경될 때 종료시간 자동 계산
  useEffect(() => {
    if (!isPastHistory && startTime && startTime.includes(':')) {
      const calculatedEndTime = calculateEndTime(startTime, usageTime, bufferTime);
      if (calculatedEndTime) {
        setEndTime(calculatedEndTime);
      }
    }
  }, [startTime, usageTime, bufferTime, isPastHistory]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '504px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const reservationMutation = useMutation({
    mutationFn: data => updateGolfReservation(user?.token, data),
    onSuccess: () => {
      toast.success('예약이 저장되었습니다.');
    },
    onError: () => {
      toast.error('예약 저장 중 오류가 발생했습니다.');
    },
  });

  const handleSave = () => {
    // 시간 문자열을 날짜+시간 형식으로 변환
    const formattedStartTime = formatTimeToDateTime(startTime, reservationData?.startTime);
    const formattedEndTime = formatTimeToDateTime(endTime, reservationData?.endTime);

    const data = {
      reservationId: reservationData?.reservationId,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      usageMinute: usageTime,
      bufferMinute: bufferTime,
    };
    reservationMutation.mutate(data);
    handleClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={modalStyle}>
          {/* 헤더 */}
          <Box
            sx={{
              height: '56px',
              backgroundColor: '#262626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '32px',
            }}>
            <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#fff' }}>
              {isPastHistory ? '이용 내역' : '예약 변경 및 취소'}
            </Typography>
            <CloseIcon sx={{ color: '#fff', width: '32px', height: '32px', cursor: 'pointer' }} onClick={handleClose} />
          </Box>

          {/* 본문 */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
              bgcolor: '#fff',
              width: '504px',
              alignSelf: 'center',
            }}>
            {/* 회원명 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>회원명</Typography>
              <Box
                sx={{
                  border: '1px solid #D9D9D9',
                  borderRadius: '8px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '16px',
                  backgroundColor: '#fff',
                }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>
                  {memberName || '게스트'}
                </Typography>
                <Box sx={{ width: '24px', height: '24px' }} />
              </Box>
            </Box>

            {/* 이용권 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>이용권</Typography>
              <Box
                sx={{
                  border: '1px solid #D9D9D9',
                  borderRadius: '8px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '16px',
                  backgroundColor: '#fff',
                }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: ticketName ? '#262626' : '#A6A6A6' }}>
                  {ticketName || '이용권'}
                </Typography>
                <Box sx={{ width: '24px', height: '24px' }} />
              </Box>
            </Box>

            {/* 이용시간, 여유시간, 시작시간, 종료시간 */}
            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
              {/* 이용시간 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>이용시간</Typography>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <TextField
                    type="number"
                    value={usageTime}
                    onChange={e => setUsageTime(e.target.value)}
                    disabled={isPastHistory}
                    sx={{
                      width: '96px',
                      '& .MuiInputBase-root': {
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: isPastHistory ? '#F2F2F2' : 'transparent',
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>분</Typography>
                </Box>
              </Box>

              {/* 여유시간 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>여유시간</Typography>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <TextField
                    type="number"
                    value={bufferTime}
                    onChange={e => setBufferTime(e.target.value)}
                    disabled={isPastHistory}
                    sx={{
                      width: '96px',
                      '& .MuiInputBase-root': {
                        height: '48px',
                        borderRadius: '8px',
                        backgroundColor: isPastHistory ? '#F2F2F2' : 'transparent',
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>분</Typography>
                </Box>
              </Box>

              {/* 시작시간 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>시작시간</Typography>
                <TextField
                  value={startTime}
                  onChange={e => {
                    const formattedValue = formatTimeInput(e.target.value);
                    setStartTime(formattedValue);
                  }}
                  placeholder="17:30"
                  disabled={isPastHistory}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: isPastHistory ? '#F2F2F2' : 'transparent',
                    },
                  }}
                />
              </Box>

              {/* 종료시간 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>종료시간</Typography>
                <TextField
                  value={endTime}
                  disabled={true}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: '#F2F2F2',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* 이용권 잔여시간 & 만료일 */}
            <Box sx={{ display: 'flex', gap: '16px' }}>
              {/* 이용권 잔여시간 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>이용권 잔여시간</Typography>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Box sx={{ width: '96px', height: '48px', borderRadius: '8px', backgroundColor: '#F2F2F2' }}>
                    <Typography sx={{ fontSize: '16px', color: '#262626', p: '12px' }}>{remainingTime}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>시간 남음</Typography>
                </Box>
              </Box>

              {/* 이용권 만료일 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>이용권 만료일</Typography>
                <Box sx={{ width: '259px', height: '48px', borderRadius: '8px', backgroundColor: '#F2F2F2' }}>
                  <Typography sx={{ fontSize: '16px', color: '#262626', p: '12px' }}>{expirationDate}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 하단 버튼 */}
          <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'center', p: '32px', bgcolor: '#fff' }}>
            <Button
              sx={{
                backgroundColor: '#D9D9D9',
                width: '120px',
                height: '48px',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: '600',
                color: '#262626',
                '&:hover': { backgroundColor: '#D9D9D9' },
              }}
              onClick={handleClose}>
              취소
            </Button>
            {!isPastHistory && (
              <>
                <Button
                  sx={{
                    backgroundColor: '#EE735A',
                    width: '120px',
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#EE735A' },
                  }}
                  onClick={() => setDeleteModalOpen(true)}>
                  예약취소
                </Button>
                <Button
                  sx={{
                    backgroundColor: '#0F7F85',
                    width: '120px',
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#0F7F85' },
                  }}
                  onClick={() => handleSave()}>
                  예약저장
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Modal>

      {/* 예약 취소 확인 모달 - Modal 밖에 렌더링 */}
      <GolfReservationDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        reservationId={reservationData?.reservationId}
        onSuccess={() => {
          setDeleteModalOpen(false);
          handleClose();
        }}
        isCancelMode={true}
      />
    </>
  );
};

export default GolfReservationManageModal;
